/**
 * SCRUM-422: página pública de claim de lista de espera — el cliente llega
 * acá desde el link del email "Se liberó un turno" y puede tomarlo o
 * liberarlo para el siguiente en la fila. Sin sesión, autorizado por el
 * accessToken en la URL.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/market_/$slug/turno-liberado/$claimId/$token")({
  component: WaitlistClaimPage,
  head: () => ({ meta: [{ title: "Tu cupo en lista de espera" }] }),
});

type ClaimView = {
  id: string;
  slotDate: string;
  slotTime: string;
  expiresAt: string | null;
  serviceName: string;
  professionalName: string;
};

function WaitlistClaimPage() {
  const { slug, claimId, token } = Route.useParams();
  const [loading, setLoading] = useState(true);
  const [claim, setClaim] = useState<ClaimView | null>(null);
  const [tenantName, setTenantName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<"confirmed" | "rejected" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`/api/market/${slug}/waitlist-claim/${claimId}?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) {
          setError(d.error ?? "No se pudo cargar el cupo");
          return;
        }
        setClaim(d.claim);
        setTenantName(d.tenantName);
      })
      .catch(() => setError("Error de red"))
      .finally(() => setLoading(false));
  }, [slug, claimId, token]);

  async function act(action: "confirm" | "reject") {
    setSubmitting(true);
    try {
      const res = await fetch(`/api/market/${slug}/waitlist-claim/${claimId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, action }),
      });
      const d = await res.json();
      if (!res.ok) {
        setError(d.error ?? "No se pudo procesar");
        return;
      }
      setResolved(action === "confirm" ? "confirmed" : "rejected");
    } catch {
      setError("Error de red");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-6 px-4 py-16 text-center">
      {loading ? (
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      ) : error ? (
        <>
          <h1 className="text-xl font-bold">No pudimos procesar tu cupo</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </>
      ) : resolved === "confirmed" ? (
        <>
          <h1 className="text-2xl font-bold text-primary">¡Turno confirmado!</h1>
          <p className="text-sm text-muted-foreground">Te esperamos. Te mandamos la confirmación por email.</p>
        </>
      ) : resolved === "rejected" ? (
        <>
          <h1 className="text-xl font-bold">Listo, gracias por avisarnos</h1>
          <p className="text-sm text-muted-foreground">Le ofrecimos el cupo a la siguiente persona en la fila.</p>
        </>
      ) : claim ? (
        <>
          <h1 className="text-2xl font-bold">¡Se liberó tu turno!</h1>
          <p className="text-sm text-muted-foreground">
            {tenantName} tiene un cupo disponible para vos.
          </p>
          <div className="w-full space-y-1 rounded-xl border border-border bg-card p-5 text-left">
            <p className="font-semibold">{claim.serviceName}</p>
            <p className="text-sm text-muted-foreground">{claim.professionalName}</p>
            <p className="text-sm">
              {claim.slotDate} · {claim.slotTime.slice(0, 5)} hs
            </p>
          </div>
          <div className="flex w-full gap-3">
            <button
              type="button"
              disabled={submitting}
              className="h-11 flex-1 rounded-md border border-border text-sm font-medium disabled:opacity-60"
              onClick={() => void act("reject")}
            >
              No puedo
            </button>
            <button
              type="button"
              disabled={submitting}
              className="h-11 flex-1 rounded-md bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-60"
              onClick={() => void act("confirm")}
              data-testid="waitlist-claim-confirm"
            >
              {submitting ? "Confirmando…" : "Tomar este turno"}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
